import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { GetRoleResponse, Role } from "api/controlPlaneApi";
import {
  addRole,
  deleteRole,
  editRole,
  fetchActions,
  fetchResources,
  fetchRoleDetails,
  fetchRoles,
} from "./rolesThunks";

export interface RolesState {
  loadingRoles: boolean;
  loadingRoleDetails: boolean;
  loadingDeleteRole: boolean;
  loadingAddRole: boolean;
  loadingEditRole: boolean;
  loadingActions: boolean;
  loadingResources: boolean;
  roles: Role[];
  roleDetails: GetRoleResponse | null;
  actions: string[];
  resources: string[];
}

const initialState: RolesState = {
  loadingRoles: false,
  loadingRoleDetails: false,
  loadingDeleteRole: false,
  loadingAddRole: false,
  loadingEditRole: false,
  loadingActions: false,
  loadingResources: false,
  roles: [],
  roleDetails: null,
  actions: [],
  resources: [],
};

export const rolesSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    setLoadingRoles: (state, action: PayloadAction<boolean>) => {
      state.loadingRoles = action.payload;
    },
    setLoadingRoleDetails: (state, action: PayloadAction<boolean>) => {
      state.loadingRoleDetails = action.payload;
    },
    setLoadingDeleteRole: (state, action: PayloadAction<boolean>) => {
      state.loadingDeleteRole = action.payload;
    },
    setLoadingAddRole: (state, action: PayloadAction<boolean>) => {
      state.loadingAddRole = action.payload;
    },
    setLoadingActions: (state, action: PayloadAction<boolean>) => {
      state.loadingActions = action.payload;
    },
    setLoadingResources: (state, action: PayloadAction<boolean>) => {
      state.loadingResources = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loadingRoles = false;
        state.roles = action.payload;
      })
      .addCase(fetchRoles.pending, (state) => {
        state.loadingRoles = true;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loadingRoles = false;
        state.roles = [];
      })
      .addCase(fetchRoleDetails.fulfilled, (state, action) => {
        state.loadingRoleDetails = false;
        state.roleDetails = action.payload;
      })
      .addCase(fetchRoleDetails.pending, (state) => {
        state.loadingRoles = true;
      })
      .addCase(fetchRoleDetails.rejected, (state, action) => {
        state.loadingRoleDetails = false;
        state.roleDetails = null;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.loadingDeleteRole = false;
      })
      .addCase(deleteRole.pending, (state) => {
        state.loadingDeleteRole = true;
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.loadingDeleteRole = false;
      })
      .addCase(addRole.fulfilled, (state, action) => {
        state.loadingAddRole = false;
      })
      .addCase(addRole.pending, (state) => {
        state.loadingAddRole = true;
      })
      .addCase(addRole.rejected, (state, action) => {
        state.loadingAddRole = false;
      })
      .addCase(editRole.fulfilled, (state, action) => {
        state.loadingEditRole = false;
      })
      .addCase(editRole.pending, (state) => {
        state.loadingEditRole = true;
      })
      .addCase(editRole.rejected, (state, action) => {
        state.loadingEditRole = false;
      })
      .addCase(fetchActions.fulfilled, (state, action) => {
        state.actions = action.payload;
        state.loadingActions = false;
      })
      .addCase(fetchActions.pending, (state) => {
        state.loadingActions = true;
      })
      .addCase(fetchActions.rejected, (state, action) => {
        state.actions = [];
        state.loadingActions = false;
      })
      .addCase(fetchResources.fulfilled, (state, action) => {
        state.resources = action.payload;
        state.loadingResources = false;
      })
      .addCase(fetchResources.pending, (state) => {
        state.loadingResources = true;
      })
      .addCase(fetchResources.rejected, (state, action) => {
        state.resources = [];
        state.loadingResources = false;
      });
  },
});

// Reducer actions
export const {
  setLoadingRoles,
  setLoadingRoleDetails,
  setLoadingDeleteRole,
  setLoadingAddRole,
  setLoadingActions,
  setLoadingResources,
} = rolesSlice.actions;

// Reducer selectors

export const roles = (state: RootState) => state.roles.roles;
export const roleDetails = (state: RootState) => state.roles.roleDetails;
export const loadingRoles = (state: RootState) => state.roles.loadingRoles;
export const loadingRoleDetails = (state: RootState) =>
  state.roles.loadingRoleDetails;
export const loadingDeleteRole = (state: RootState) =>
  state.roles.loadingDeleteRole;
export const loadingAddRole = (state: RootState) => state.roles.loadingAddRole;
export const loadingEditRole = (state: RootState) =>
  state.roles.loadingEditRole;
export const loadingActions = (state: RootState) => state.roles.loadingActions;
export const loadingResources = (state: RootState) =>
  state.roles.loadingResources;
export const actions = (state: RootState) => state.roles.actions;
export const resources = (state: RootState) => state.roles.resources;

export default rolesSlice.reducer;
